import { Hint, InputCell } from 'components/cooperation-form/styled';
import { Select, TextField } from 'kl-b2c-ui-kit';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getHandbookRegistrationFormRules } from './rules';
import { RowsWrapper } from './styled';
import { HowAreYouGoingToUse } from 'types';

const INPUT_CELL_STYLES = { width: '100%', margin: '0 0 8px 0' };

const HandbookRegistrationRows: FC = () => {
    const {
        control,
        watch,
        formState: { errors },
    } = useFormContext();

    const { t } = useTranslation(['pages/courses', 'common/errors']);

    const selectStyles = {
        container: {
            height: '46px',
        },
        placeholder: {
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '16px',
        },
    };

    const intentItems = [
        {
            key: t(
                `pages/courses:handbookuse-${HowAreYouGoingToUse.TeachingClassroom}`
            ),
            value: HowAreYouGoingToUse.TeachingClassroom,
        },
        {
            key: t(
                `pages/courses:handbookuse-${HowAreYouGoingToUse.TeachingAtHome}`
            ),
            value: HowAreYouGoingToUse.TeachingAtHome,
        },
        {
            key: t(`pages/courses:handbookuse-${HowAreYouGoingToUse.Personal}`),
            value: HowAreYouGoingToUse.Personal,
        },
        {
            key: t(`pages/courses:handbookuse-${HowAreYouGoingToUse.Other}`),
            value: HowAreYouGoingToUse.Other,
        },
    ];

    const rules = getHandbookRegistrationFormRules(t);
    const howAreYouGoingToUseWatch = watch('howAreYouGoingToUse');

    return (
        <RowsWrapper>
            <InputCell styles={INPUT_CELL_STYLES}>
                <Controller
                    name={'name'}
                    control={control}
                    rules={rules['name']}
                    render={({ field: { onChange } }) => (
                        <TextField
                            fullWidth
                            variant={'border'}
                            type={'text'}
                            onChange={onChange}
                            placeholder={t('pages/courses:name')}
                            error={!!errors.name}
                            atSelector={'handbookRegistrationForm_name'}
                        />
                    )}
                />
                {errors.name && <Hint>{errors.name?.message as string}</Hint>}
            </InputCell>

            <InputCell styles={INPUT_CELL_STYLES}>
                <Controller
                    name={'email'}
                    control={control}
                    rules={rules['email']}
                    render={({ field: { onChange } }) => (
                        <TextField
                            fullWidth
                            variant={'border'}
                            type={'text'}
                            onChange={onChange}
                            placeholder={t('pages/courses:email')}
                            error={!!errors.email}
                            atSelector={'handbookRegistrationForm_email'}
                        />
                    )}
                />
                {errors.email && <Hint>{errors.email?.message as string}</Hint>}
            </InputCell>

            <InputCell styles={INPUT_CELL_STYLES}>
                <Controller
                    name={'profession'}
                    control={control}
                    rules={rules['profession']}
                    render={({ field: { onChange } }) => (
                        <TextField
                            fullWidth
                            variant={'border'}
                            type={'text'}
                            onChange={onChange}
                            placeholder={t('pages/courses:profession')}
                            error={!!errors.profession}
                            atSelector={'handbookRegistrationForm_profession'}
                        />
                    )}
                />
                {errors.profession && (
                    <Hint>{errors.profession?.message as string}</Hint>
                )}
            </InputCell>

            <InputCell styles={INPUT_CELL_STYLES}>
                <Controller
                    name={'country'}
                    control={control}
                    rules={rules['country']}
                    render={({ field: { onChange } }) => (
                        <TextField
                            fullWidth
                            variant={'border'}
                            type={'text'}
                            onChange={onChange}
                            placeholder={t('pages/courses:country')}
                            error={!!errors.country}
                            atSelector={'handbookRegistrationForm_country'}
                        />
                    )}
                />
                {errors.country && (
                    <Hint>{errors.country?.message as string}</Hint>
                )}
            </InputCell>

            <InputCell styles={INPUT_CELL_STYLES}>
                <Controller
                    name={'howAreYouGoingToUse'}
                    control={control}
                    rules={rules['howAreYouGoingToUse']}
                    render={({ field: { onChange } }) => (
                        <Select
                            error={!!errors?.howAreYouGoingToUse}
                            styles={selectStyles}
                            onChoose={(
                                item:
                                    | {
                                          key: string;
                                          value: string | number;
                                      }[]
                                    | undefined
                            ) => {
                                if (item && item.length) {
                                    onChange(item[0].value);
                                }
                            }}
                            placeholder={t('pages/courses:howAreYouGoingToUse')}
                            variant={'border'}
                            items={intentItems}
                            atSelector={{
                                container:
                                    'handbookRegistrationForm_howAreYouGoingToUse',
                                optionItem:
                                    'handbookRegistrationForm_howAreYouGoingToUse_item',
                                optionsContainer:
                                    'handbookRegistrationForm_howAreYouGoingToUse_optionsContainer',
                            }}
                        />
                    )}
                />
                {errors.howAreYouGoingToUse && (
                    <Hint>{errors.howAreYouGoingToUse?.message as string}</Hint>
                )}
            </InputCell>

            {howAreYouGoingToUseWatch === HowAreYouGoingToUse.Other && (
                <InputCell styles={INPUT_CELL_STYLES}>
                    <Controller
                        name={'other'}
                        control={control}
                        rules={rules['other']}
                        render={({ field: { onChange } }) => (
                            <TextField
                                fullWidth
                                variant={'border'}
                                type={'text'}
                                onChange={onChange}
                                placeholder={''}
                                error={!!errors.other}
                                atSelector={'handbookRegistrationForm_other'}
                                isTextArea
                            />
                        )}
                    />
                    {errors.other && (
                        <Hint>{errors.other?.message as string}</Hint>
                    )}
                </InputCell>
            )}
        </RowsWrapper>
    );
};

export default HandbookRegistrationRows;
